import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  ViewChild
} from "@angular/core";
import { HelpAddSupportCaseService } from "@onsip/common/services/api/resources/help-add-support-case.service";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { NgForm, NonNullableFormBuilder, Validators } from "@angular/forms";
import { Subscription, map, startWith, take } from "rxjs";
import { Router } from "@angular/router";
import { ModalMaterialComponent } from "../../shared/components/modal/modal-material.component";
import { MatDialog } from "@angular/material/dialog";
import { AccountService } from "@onsip/common/services/api/resources/account/account.service";

export enum IssueType {
  Configuration = "Configuration",
  TechnicalSupport = "Technical Support",
  FeatureRequest = "Feature Request",
  Billing = "Billing",
  PaymentOptions = "Payment Options",
  CancelAccount = "Cancel Account",
  Other = "Other"
}

@Component({
  selector: "onsip-admin-support-submit-ticket",
  templateUrl: "./admin-support-submit-ticket.component.html",
  styleUrls: ["./admin-support-submit-ticket.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminSupportSubmitTicketComponent implements OnDestroy {
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;
  @ViewChild("form") ngForm!: NgForm;
  @Input() showSubject = true;
  @Input() showHeader = true;

  private unsubscriber = new Subscription();

  isSelfServiceAcc$ = this.accountService.accountState.pipe(
    map(res => !res[0].noPackageAssignment || res[0].productcode !== "PhonePromo")
  );

  issueType = "";

  issueTypes$ = this.isSelfServiceAcc$.pipe(
    map(isSelfServiceAcc => {
      if (isSelfServiceAcc) {
        return Object.values(IssueType);
      }
      return Object.values(IssueType).filter(item => item !== IssueType.CancelAccount);
    })
  );

  reasonTypes = [
    "Services are too expensive",
    "I had an issue with services",
    "I found a better service",
    "Price: too expensive",
    "Porting issues",
    "Too complicated to use",
    "Moving to a different company",
    "Feature limitations",
    "Other"
  ];

  submitTicketForm = this.fb.group({
    issueType: ["Configuration"],
    subject: [""],
    details: [""]
  });

  get isIssueTypeCancelAccount() {
    return this.submitTicketForm.value.issueType === IssueType.CancelAccount;
  }

  selectReasonVisible$ = this.submitTicketForm.valueChanges.pipe(
    startWith(() => this.submitTicketForm.value),
    map(() => this.isIssueTypeCancelAccount)
  );

  constructor(
    private helpAddSupportCaseService: HelpAddSupportCaseService,
    private snackbar: SnackbarService,
    private fb: NonNullableFormBuilder,
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private accountService: AccountService
  ) {
    this.issueType = this.router.getCurrentNavigation()?.extras?.state?.issueType;
    if (this.issueType) {
      this.submitTicketForm.patchValue({ issueType: this.issueType }, { emitEvent: true });
      this.submitTicketForm.updateValueAndValidity({ emitEvent: true });
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  clearForm(): void {
    this.ngForm.resetForm({
      issueType: "",
      subject: "",
      details: ""
    });
  }

  /** if a user clicks on description text field, clear any details error */
  handleDetailFocus(): void {
    // eslint-disable-next-line no-null/no-null
    this.submitTicketForm.controls.details.setErrors(null);
  }

  handleSubmit() {
    this.validateForm();
    if (this.submitTicketForm.valid) {
      if (this.isIssueTypeCancelAccount) {
        this.showDialogConfirm();
      } else {
        this.submit();
      }
    }
  }

  submit(): void {
    const { issueType, details, subject } = this.submitTicketForm.value;
    const params = {
      Description: details,
      Subject: this.showSubject ? subject : "Admin Portal Ticket Created",
      Reason: issueType === IssueType.Configuration ? "Initial Setup" : issueType,
      Type: issueType === IssueType.Configuration ? "Technical Support" : issueType
    };
    this.helpAddSupportCaseService.HelpAddSupportCase(params).then(result => {
      if (result.status === "success") {
        this.snackbar.openSnackBar("Your ticket has been successfully submitted.", "success");
        if (this.isIssueTypeCancelAccount) {
          this.showDialogInfo();
        }
        this.clearForm();
      } else {
        this.snackbar.openSnackBar(result.data.message, "error");
        this.cdRef.markForCheck();
      }
    });
  }

  private validateForm() {
    // the description field is required if the user submits a ticket
    if (!this.submitTicketForm.controls.details.value) {
      this.submitTicketForm.controls.details.setErrors(Validators.required);
      return;
    }
  }

  private showDialogConfirm() {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          maxWidth: "341px",
          data: {
            title: "Cancel account",
            message:
              "By cancelling, you’ll lose access to your phone numbers, all stored data (like voicemails and call logs), 911 service and other features.",
            primaryBtnText: "Cancel account",
            secondaryBtnText: "Keep account",
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.submit();
          }
        })
    );
  }

  private showDialogInfo() {
    this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      maxWidth: "341px",
      data: {
        title: "Your request to cancel your account has been submitted",
        message:
          "You can track the progress of your account cancel in the support ticket section of the Support page.</br> </br> It will take up to 5 business days to complete account cancellation. </br> </br>We appreciate your business.",
        primaryBtnText: "Ok",
        primaryBtnFlat: true,
        showOnlyPrimaryBtn: true
      }
    });
  }
}
